import type { Vec } from '../destruction/structure';
import { GeometryBuilder } from './geometry-builder';

interface Rectangle {
  u: number;
  U: number;
  v: number;
  V: number;
}
interface Plane {
  axis: number;
  sign: number;
  at: number;
  color: number;
  rectangles: Rectangle[];
}

/** Merge only touching coplanar rectangles of exactly the same normal and
 * vertex colour. Coordinates match the Float32 values sent to the GPU. Holes,
 * unequal colours, detail pieces and non-rectangular faces remain unchanged. */
export class CoplanarQuads extends GeometryBuilder {
  private planes = new Map<string, Plane>();
  override quad(a: Vec, b: Vec, c: Vec, d: Vec, n: Vec, color: number): void {
    const axis = Math.abs(n.x) === 1 ? 0 : Math.abs(n.y) === 1 ? 1 : Math.abs(n.z) === 1 ? 2 : -1;
    const ux = b.x - a.x,
      uy = b.y - a.y,
      uz = b.z - a.z,
      vx = d.x - a.x,
      vy = d.y - a.y,
      vz = d.z - a.z;
    if (
      axis < 0 ||
      (axis !== 0 && n.x !== 0) ||
      (axis !== 1 && n.y !== 0) ||
      (axis !== 2 && n.z !== 0) ||
      (uy * vz - uz * vy) * n.x + (uz * vx - ux * vz) * n.y + (ux * vy - uy * vx) * n.z <= 0
    ) {
      super.quad(a, b, c, d, n, color);
      return;
    }
    const coordinate = (p: Vec, axis: number) => Math.fround(axis === 0 ? p.x : axis === 1 ? p.y : p.z);
    const at = coordinate(a, axis),
      uAxis = (axis + 1) % 3,
      vAxis = (axis + 2) % 3;
    const au = coordinate(a, uAxis),
      av = coordinate(a, vAxis),
      bu = coordinate(b, uAxis),
      bv = coordinate(b, vAxis),
      cu = coordinate(c, uAxis),
      cv = coordinate(c, vAxis),
      du = coordinate(d, uAxis),
      dv = coordinate(d, vAxis),
      u = Math.min(au, bu, cu, du),
      U = Math.max(au, bu, cu, du),
      v = Math.min(av, bv, cv, dv),
      V = Math.max(av, bv, cv, dv);
    if (
      u === U ||
      v === V ||
      coordinate(b, axis) !== at ||
      coordinate(c, axis) !== at ||
      coordinate(d, axis) !== at ||
      !(
        (au === bu && bv === cv && cu === du && dv === av && au !== cu && av !== bv) ||
        (av === bv && bu === cu && cv === dv && du === au && au !== bu && av !== cv)
      )
    ) {
      super.quad(a, b, c, d, n, color);
      return;
    }
    const sign = axis === 0 ? n.x : axis === 1 ? n.y : n.z,
      key = `${axis}:${sign}:${at}:${color}`;
    let plane = this.planes.get(key);
    if (!plane) this.planes.set(key, (plane = { axis, sign, at, color, rectangles: [] }));
    plane.rectangles.push({ u, U, v, V });
  }
  flush(): void {
    for (const plane of this.planes.values()) {
      let rectangles = plane.rectangles;
      // Horizontal then vertical exact adjacency. Never fill a missing corner
      // or collapse overlapping surfaces, which could change blending/shadows.
      for (const vertical of [false, true]) {
        const low = vertical ? 'v' : 'u',
          high = vertical ? 'V' : 'U',
          crossLow = vertical ? 'u' : 'v',
          crossHigh = vertical ? 'U' : 'V';
        rectangles.sort(
          (a, b) => a[crossLow] - b[crossLow] || a[crossHigh] - b[crossHigh] || a[low] - b[low],
        );
        const merged: Rectangle[] = [];
        for (const rect of rectangles) {
          const prior = merged.at(-1);
          if (
            prior &&
            prior[crossLow] === rect[crossLow] &&
            prior[crossHigh] === rect[crossHigh] &&
            prior[high] === rect[low]
          )
            prior[high] = rect[high];
          else merged.push(rect);
        }
        rectangles = merged;
      }
      const { axis, sign, at, color } = plane,
        n = { x: 0, y: 0, z: 0 };
      if (axis === 0) n.x = sign;
      else if (axis === 1) n.y = sign;
      else n.z = sign;
      const point = (u: number, v: number): Vec =>
        axis === 0 ? { x: at, y: u, z: v } : axis === 1 ? { x: v, y: at, z: u } : { x: u, y: v, z: at };
      for (const r of rectangles) {
        const a = point(r.u, r.v),
          b = point(r.U, r.v),
          c = point(r.U, r.V),
          d = point(r.u, r.V);
        if (sign > 0) super.quad(a, b, c, d, n, color);
        else super.quad(a, d, c, b, n, color);
      }
    }
    this.planes.clear();
  }
  override *geometrySteps(chunk = 49152) {
    this.flush();
    return yield* super.geometrySteps(chunk);
  }
}
